/**
 * Grouping Utilities for Smart Grid
 *
 * Provides functions for grouping data by fields, managing group expansion state,
 * and calculating group aggregates.
 *
 * @example
 * ```typescript
 * // Group orders by status
 * const grouped = groupDataByFields(orders, ['statusId']);
 *
 * // Group by multiple fields (warehouse then status)
 * const multiGrouped = groupDataByFields(orders, ['warehouse.name', 'statusId']);
 *
 * // Flatten for rendering
 * const flatData = flattenGroupedData(grouped, expansionState);
 * ```
 */

import { getNestedValue } from './field-accessor.util';

/**
 * Group node representing a group of data or a data item
 */
export interface GroupNode<T> {
  /**
   * Unique key for this group
   */
  key: string;

  /**
   * Type of node
   */
  type: 'group' | 'data';

  /**
   * Group level (0 = top level, 1 = nested, etc.)
   */
  level: number;

  /**
   * Field this group is based on
   */
  field?: string;

  /**
   * Group value (the actual value being grouped by)
   */
  value?: any;

  /**
   * Display label for the group
   */
  label: string;

  /**
   * Number of items in this group (including nested)
   */
  count: number;

  /**
   * Direct children count (not including nested)
   */
  directCount?: number;

  /**
   * Children nodes (can be groups or data)
   */
  children: GroupNode<T>[];

  /**
   * Actual data item (only for type='data')
   */
  data?: T;

  /**
   * Parent group key
   */
  parentKey?: string;

  /**
   * Aggregates for this group
   */
  aggregates?: Record<string, any>;

  /**
   * Is this group expanded (managed separately in state)
   */
  expanded?: boolean;
}

/**
 * Group expansion state
 * Maps group key to expanded boolean
 */
export type GroupExpansionState = Map<string, boolean>;

/**
 * Aggregate function type
 */
export type AggregateFunction = 'count' | 'sum' | 'avg' | 'min' | 'max' | 'first' | 'last';

/**
 * Aggregate configuration
 */
export interface AggregateConfig {
  field: string;
  function: AggregateFunction;
  label?: string;
}

/**
 * Group data by one or multiple fields
 *
 * @param data - Array of data to group
 * @param groupByFields - Fields to group by (in order)
 * @param level - Current nesting level (internal use)
 * @param parentKey - Parent group key (internal use)
 * @returns Array of group nodes
 */
export function groupDataByFields<T>(
  data: T[],
  groupByFields: string[],
  level: number = 0,
  parentKey: string = ''
): GroupNode<T>[] {
  if (!data || data.length === 0) {
    return [];
  }

  // If no more fields to group by, return data nodes
  if (!groupByFields || groupByFields.length === 0 || level >= groupByFields.length) {
    return data.map((item, index) => ({
      key: `${parentKey}_data_${index}`,
      type: 'data' as const,
      level,
      label: '',
      count: 1,
      children: [],
      data: item,
      parentKey,
    }));
  }

  const currentField = groupByFields[level];
  const grouped = new Map<any, T[]>();

  // Group data by current field
  data.forEach(item => {
    const value = getNestedValue(item, currentField);
    const key = value !== null && value !== undefined ? value : '(Sin valor)';

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(item);
  });

  // Create group nodes
  const result: GroupNode<T>[] = [];

  grouped.forEach((items, value) => {
    const groupKey = `${parentKey}_${currentField}_${value}`;
    const children = groupDataByFields(items, groupByFields, level + 1, groupKey);

    result.push({
      key: groupKey,
      type: 'group',
      level,
      field: currentField,
      value,
      label: formatGroupLabel(currentField, value),
      count: items.length,
      directCount: items.length,
      children,
      parentKey,
      expanded: false, // Default to collapsed
    });
  });

  // Sort groups by label
  result.sort((a, b) => String(a.label).localeCompare(String(b.label)));

  return result;
}

/**
 * Format group label for display
 */
function formatGroupLabel(field: string, value: any): string {
  if (value === null || value === undefined || value === '') {
    return '(Sin valor)';
  }

  // If value is a complex object, try to get a string representation
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

/**
 * Flatten grouped data for rendering
 * Returns only visible nodes based on expansion state
 *
 * @param groupedData - Grouped data tree
 * @param expansionState - Map of group keys to expanded state
 * @returns Flat array of visible nodes
 */
export function flattenGroupedData<T>(
  groupedData: GroupNode<T>[],
  expansionState: GroupExpansionState
): GroupNode<T>[] {
  const result: GroupNode<T>[] = [];

  function traverse(nodes: GroupNode<T>[]): void {
    for (const node of nodes) {
      // Always add the node itself
      result.push(node);

      // If it's a group and it's expanded, add its children
      if (node.type === 'group') {
        const isExpanded = expansionState.get(node.key) ?? false;
        node.expanded = isExpanded; // Update node state

        if (isExpanded && node.children.length > 0) {
          traverse(node.children);
        }
      }
    }
  }

  traverse(groupedData);
  return result;
}

/**
 * Get unique group key
 */
export function getGroupKey(field: string, value: any, parentKey: string = ''): string {
  const valueStr = value !== null && value !== undefined ? String(value) : 'null';
  return `${parentKey}_${field}_${valueStr}`;
}

/**
 * Expand a specific group
 */
export function expandGroup(groupKey: string, expansionState: GroupExpansionState): void {
  expansionState.set(groupKey, true);
}

/**
 * Collapse a specific group
 */
export function collapseGroup(groupKey: string, expansionState: GroupExpansionState): void {
  expansionState.set(groupKey, false);
}

/**
 * Toggle group expansion
 */
export function toggleGroupExpansion(groupKey: string, expansionState: GroupExpansionState): void {
  const current = expansionState.get(groupKey) ?? false;
  expansionState.set(groupKey, !current);
}

/**
 * Expand all groups recursively
 */
export function expandAllGroups<T>(
  groupedData: GroupNode<T>[],
  expansionState: GroupExpansionState
): void {
  function traverse(nodes: GroupNode<T>[]): void {
    for (const node of nodes) {
      if (node.type === 'group') {
        expansionState.set(node.key, true);
        if (node.children.length > 0) {
          traverse(node.children);
        }
      }
    }
  }

  traverse(groupedData);
}

/**
 * Collapse all groups recursively
 */
export function collapseAllGroups<T>(
  groupedData: GroupNode<T>[],
  expansionState: GroupExpansionState
): void {
  function traverse(nodes: GroupNode<T>[]): void {
    for (const node of nodes) {
      if (node.type === 'group') {
        expansionState.set(node.key, false);
        if (node.children.length > 0) {
          traverse(node.children);
        }
      }
    }
  }

  traverse(groupedData);
}

/**
 * Check if a group is expanded
 */
export function isGroupExpanded(groupKey: string, expansionState: GroupExpansionState): boolean {
  return expansionState.get(groupKey) ?? false;
}

/**
 * Get group level (depth in hierarchy)
 */
export function getGroupLevel<T>(node: GroupNode<T>): number {
  return node.level;
}

/**
 * Calculate aggregates for a group
 *
 * @param node - Group node to calculate aggregates for
 * @param aggregateConfigs - Configuration for aggregates
 * @returns Object with aggregate results
 */
export function calculateGroupAggregates<T>(
  node: GroupNode<T>,
  aggregateConfigs: AggregateConfig[]
): Record<string, any> {
  const results: Record<string, any> = {};

  if (node.type !== 'group') {
    return results;
  }

  // Get all data items in this group (recursively)
  const allData = getAllDataItems(node);

  for (const config of aggregateConfigs) {
    const { field, function: func, label } = config;
    const key = label || `${field}_${func}`;

    switch (func) {
      case 'count':
        results[key] = allData.length;
        break;

      case 'sum':
        results[key] = allData.reduce((sum, item) => {
          const value = getNestedValue(item, field);
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);
        break;

      case 'avg':
        const sum = allData.reduce((s, item) => {
          const value = getNestedValue(item, field);
          return s + (typeof value === 'number' ? value : 0);
        }, 0);
        results[key] = allData.length > 0 ? sum / allData.length : 0;
        break;

      case 'min':
        const values = allData
          .map(item => getNestedValue(item, field))
          .filter(v => typeof v === 'number');
        results[key] = values.length > 0 ? Math.min(...values) : null;
        break;

      case 'max':
        const maxValues = allData
          .map(item => getNestedValue(item, field))
          .filter(v => typeof v === 'number');
        results[key] = maxValues.length > 0 ? Math.max(...maxValues) : null;
        break;

      case 'first':
        results[key] = allData.length > 0 ? getNestedValue(allData[0], field) : null;
        break;

      case 'last':
        results[key] = allData.length > 0 ? getNestedValue(allData[allData.length - 1], field) : null;
        break;
    }
  }

  return results;
}

/**
 * Get all data items from a group node (recursively)
 */
function getAllDataItems<T>(node: GroupNode<T>): T[] {
  const items: T[] = [];

  function traverse(n: GroupNode<T>): void {
    if (n.type === 'data' && n.data) {
      items.push(n.data);
    } else if (n.type === 'group' && n.children) {
      for (const child of n.children) {
        traverse(child);
      }
    }
  }

  traverse(node);
  return items;
}

/**
 * Get total count of groups at all levels
 */
export function getTotalGroupCount<T>(groupedData: GroupNode<T>[]): number {
  let count = 0;

  function traverse(nodes: GroupNode<T>[]): void {
    for (const node of nodes) {
      if (node.type === 'group') {
        count++;
        if (node.children.length > 0) {
          traverse(node.children);
        }
      }
    }
  }

  traverse(groupedData);
  return count;
}

/**
 * Get count of expanded groups
 */
export function getExpandedGroupCount<T>(
  groupedData: GroupNode<T>[],
  expansionState: GroupExpansionState
): number {
  let count = 0;

  function traverse(nodes: GroupNode<T>[]): void {
    for (const node of nodes) {
      if (node.type === 'group') {
        if (expansionState.get(node.key)) {
          count++;
        }
        if (node.children.length > 0) {
          traverse(node.children);
        }
      }
    }
  }

  traverse(groupedData);
  return count;
}

/**
 * Get maximum depth of grouping
 */
export function getMaxGroupDepth<T>(groupedData: GroupNode<T>[]): number {
  let maxDepth = 0;

  function traverse(nodes: GroupNode<T>[], depth: number): void {
    for (const node of nodes) {
      maxDepth = Math.max(maxDepth, depth);
      if (node.children.length > 0) {
        traverse(node.children, depth + 1);
      }
    }
  }

  traverse(groupedData, 0);
  return maxDepth;
}
